import React, { useState, useEffect } from 'react';
import {
  Building,
  ShieldCheck,
  Trash2,
  AlertTriangle,
  RefreshCw,
  Database,
  Server,
  CheckCircle2,
  XCircle,
  Eye,
  EyeOff,
  CheckSquare,
  Square,
  Layers,
  MapPin,
  Truck,
  Users,
  Calendar,
  Package,
  Activity,
  Download,
  Upload
} from 'lucide-react';
import MapboxStyleSelector from '../components/MapboxStyleSelector';

export default function ProfilePage({ isPg, onReloadData, currentTenantId, currentTenantName, currentUser }) {
  // DB Status State
  const [dbStatus, setDbStatus] = useState(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);

  // Table Wipe Modal State
  const [showWipeModal, setShowWipeModal] = useState(false);
  const [tablesList, setTablesList] = useState([]);
  const [selectedTables, setSelectedTables] = useState({});
  const [isLoadingTables, setIsLoadingTables] = useState(false);
  const [isWiping, setIsWiping] = useState(false);
  const [wipeStatus, setWipeStatus] = useState(null);

  // Fetch DB Status & Tables on Mount and on Tenant Change
  const fetchDbStatus = async () => {
    setIsLoadingStatus(true);
    try {
      const res = await fetch('/api/database/status');
      const data = await res.json();
      setDbStatus(data);
    } catch (err) {
      console.error('Error loading DB status:', err);
    } finally {
      setIsLoadingStatus(false);
    }
  };

  const fetchTablesList = async () => {
    setIsLoadingTables(true);
    try {
      const res = await fetch('/api/database/tables');
      const data = await res.json();
      if (data.success && Array.isArray(data.tables)) {
        setTablesList(data.tables);
        // Pre-select all tables by default as requested
        const initialSelected = {};
        data.tables.forEach(t => {
          initialSelected[t.name] = true;
        });
        setSelectedTables(initialSelected);
      }
    } catch (err) {
      console.error('Error fetching tables:', err);
    } finally {
      setIsLoadingTables(false);
    }
  };

  useEffect(() => {
    fetchDbStatus();
  }, [currentTenantId]);

  const handleOpenWipeModal = () => {
    setWipeStatus(null);
    setShowWipeModal(true);
    fetchTablesList();
  };

  const handleToggleTable = (tableName) => {
    setSelectedTables(prev => ({
      ...prev,
      [tableName]: !prev[tableName]
    }));
  };

  const handleSelectAll = (select) => {
    const updated = {};
    tablesList.forEach(t => {
      updated[t.name] = select;
    });
    setSelectedTables(updated);
  };

  const handleSelectCategory = (categoryName) => {
    const updated = { ...selectedTables };
    const categoryTables = tablesList.filter(t => t.category === categoryName);
    const allSelected = categoryTables.every(t => !!updated[t.name]);
    categoryTables.forEach(t => {
      updated[t.name] = !allSelected;
    });
    setSelectedTables(updated);
  };



  const handleWipeSelected = async () => {
    const tablesToWipe = Object.keys(selectedTables).filter(k => selectedTables[k]);
    if (tablesToWipe.length === 0) {
      alert('Veuillez sélectionner au moins une table à vider.');
      return;
    }

    setIsWiping(true);
    setWipeStatus(null);

    try {
      const res = await fetch('/api/database/wipe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tables: tablesToWipe })
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setWipeStatus({
          type: 'success',
          message: `✅ ${data.wipedTables.length} table(s) vidée(s) avec succès !`
        });
        if (onReloadData) await onReloadData();
        await fetchDbStatus();
        await fetchTablesList();
      } else {
        setWipeStatus({
          type: 'error',
          message: `❌ Échec de la purge : ${data.error || 'Erreur inconnue'}`
        });
      }
    } catch (err) {
      setWipeStatus({ type: 'error', message: `❌ Erreur : ${err.message}` });
    } finally {
      setIsWiping(false);
    }
  };

  const selectedCount = Object.values(selectedTables).filter(Boolean).length;
  const selectedRowsCount = tablesList
    .filter(t => !!selectedTables[t.name])
    .reduce((sum, t) => sum + (t.count || 0), 0);

  const categories = Array.from(new Set(tablesList.map(t => t.category)));

  return (
    <div className="database-container" style={{ maxWidth: '850px', margin: '0 auto', overflowY: 'auto' }}>
      
      {/* PAGE TITLE */}
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: '1.4rem', fontWeight: '800', color: '#ffffff' }}>Paramètres du Compte & Configuration</h2>
        <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>Configuration de l'organisation, du serveur PostgreSQL et maintenance des tables.</p>
      </div>

      {/* 1. ORGANISATION CARD & STATUS */}
      <div className="overlay-card" style={{ padding: '18px 20px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
            <Building size={18} color="#38bdf8" />
            <span>Informations Organisation</span>
          </h3>

          {/* Red / Green Connectivity indicator with refresh */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 10px',
                borderRadius: '6px',
                fontSize: '0.75rem',
                fontWeight: '800',
                background: dbStatus?.connected ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                color: dbStatus?.connected ? '#22c55e' : '#ef4444',
                border: `1px solid ${dbStatus?.connected ? '#22c55e' : '#ef4444'}`
              }}
            >
              {dbStatus?.connected ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
              <span>{dbStatus?.connected ? 'Connecté' : 'Hors ligne'}</span>
            </span>

            <button
              onClick={fetchDbStatus}
              disabled={isLoadingStatus}
              title="Rafraîchir le statut de connectivité"
              style={{ background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', padding: '5px', borderRadius: '6px', cursor: 'pointer' }}
            >
              <RefreshCw size={14} className={isLoadingStatus ? 'spin' : ''} />
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '0.85rem' }}>
          <div>
            <label style={{ color: '#94a3b8', display: 'block', fontSize: '0.75rem' }}>Entreprise active</label>
            <strong style={{ color: '#f8fafc' }}>
              {currentTenantName || dbStatus?.tenantName || 'Green Future Logistics'}
            </strong>
          </div>
          <div>
            <label style={{ color: '#94a3b8', display: 'block', fontSize: '0.75rem' }}>Pays / Région</label>
            <strong style={{ color: '#f8fafc' }}>Suisse Romande (VD/GE)</strong>
          </div>
        </div>
      </div>

      {/* 2. MAPBOX STYLE SELECTOR (SIMPLIFIED DROPDOWN) */}
      <MapboxStyleSelector />

      {/* 3. GESTION DES DONNÉES & MAINTENANCE (MERGED) */}
      <div className="overlay-card" style={{ padding: '20px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px', margin: 0, color: '#ffffff' }}>
            <Database size={18} color="#a855f7" />
            <span>Gestion des Données & Maintenance</span>
          </h3>
        </div>

        <p style={{ fontSize: '0.82rem', color: '#94a3b8', margin: '0 0 16px 0', lineHeight: '1.4' }}>
          Exportez un dump SQL complet compatible pgAdmin, injectez des données, ou procédez à la purge sélective des tables de l'organisation active.
        </p>

        {wipeStatus && (
          <div
            style={{
              padding: '10px 12px',
              borderRadius: '6px',
              fontSize: '0.82rem',
              fontWeight: '700',
              marginBottom: '14px',
              background: wipeStatus.type === 'success' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
              color: wipeStatus.type === 'success' ? '#22c55e' : '#ef4444',
              border: `1px solid ${wipeStatus.type === 'success' ? '#22c55e' : '#ef4444'}`
            }}
          >
            {wipeStatus.message}
          </div>
        )}

        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Button 1: Dump SQL (pgAdmin) */}
          <button
            type="button"
            onClick={() => {
              window.location.href = '/api/database/export-sql';
            }}
            style={{
              background: 'rgba(168, 85, 247, 0.15)',
              color: '#c084fc',
              border: '1px solid #a855f7',
              padding: '8px 16px',
              height: '38px',
              fontSize: '0.82rem',
              fontWeight: '800',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            title="Télécharger un dump SQL complet compatible pgAdmin"
          >
            <Download size={15} />
            <span>Dump SQL (pgAdmin)</span>
          </button>

          {/* Button 2: SQL Pump (Greyed out / disabled) */}
          <button
            type="button"
            disabled
            style={{
              background: '#1e293b',
              color: '#64748b',
              border: '1px solid #334155',
              padding: '8px 16px',
              height: '38px',
              fontSize: '0.82rem',
              fontWeight: '700',
              borderRadius: '6px',
              cursor: 'not-allowed',
              opacity: 0.6,
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            title="SQL Pump (Bientôt disponible — En cours de développement)"
          >
            <Upload size={15} />
            <span>SQL Pump</span>
            <span style={{ fontSize: '0.65rem', background: '#334155', color: '#94a3b8', padding: '1px 6px', borderRadius: '4px', marginLeft: '4px' }}>
              Bientôt
            </span>
          </button>

          {/* Button 3: Vider la base de données... (Red) */}
          <button
            type="button"
            onClick={handleOpenWipeModal}
            style={{
              background: 'rgba(239, 68, 68, 0.15)',
              color: '#f87171',
              border: '1px solid #ef4444',
              padding: '8px 16px',
              height: '38px',
              borderRadius: '6px',
              fontSize: '0.82rem',
              fontWeight: '800',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Trash2 size={15} />
            <span>Vider la base de données...</span>
          </button>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 4. SELECTIVE TABLES WIPE POPUP MODAL */}
      {/* ========================================================================= */}
      {showWipeModal && (
        <div className="modal-overlay" style={{ zIndex: 1300 }}>
          <div
            className="overlay-card"
            style={{
              width: '680px',
              maxWidth: '92vw',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              padding: '20px',
              borderRadius: '12px',
              background: '#0f172a',
              border: '1px solid #ef4444',
              boxShadow: '0 20px 50px rgba(0,0,0,0.8)'
            }}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', borderBottom: '1px solid #334155', paddingBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertTriangle size={22} color="#ef4444" />
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800', color: '#ffffff' }}>
                  Sélection des Tables à Vider
                </h3>
              </div>
              <button
                onClick={() => setShowWipeModal(false)}
                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1.2rem', padding: '4px' }}
              >
                ✕
              </button>
            </div>

            {/* Modal Subtitle & Select/Deselect Bar */}
            <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <span style={{ fontSize: '0.78rem', color: '#cbd5e1' }}>
                Cochez les tables que vous souhaitez réinitialiser (action irréversible) :
              </span>

              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  type="button"
                  onClick={() => handleSelectAll(true)}
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#38bdf8', padding: '3px 8px', borderRadius: '4px', fontSize: '0.72rem', fontWeight: '700', cursor: 'pointer' }}
                >
                  Tout cocher
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectAll(false)}
                  style={{ background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', padding: '3px 8px', borderRadius: '4px', fontSize: '0.72rem', fontWeight: '700', cursor: 'pointer' }}
                >
                  Tout décocher
                </button>
              </div>
            </div>

            {/* Scrollable Categories of Tables */}
            <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px', display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '14px' }}>
              {isLoadingTables ? (
                <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>
                  <RefreshCw size={24} className="spin" style={{ margin: '0 auto 8px' }} />
                  <div>Chargement des tables...</div>
                </div>
              ) : (
                categories.map(cat => {
                  const catTables = tablesList.filter(t => t.category === cat);
                  const allCatSelected = catTables.every(t => !!selectedTables[t.name]);

                  return (
                    <div
                      key={cat}
                      style={{
                        background: '#1e293b',
                        borderRadius: '8px',
                        border: '1px solid #334155',
                        overflow: 'hidden'
                      }}
                    >
                      {/* Category Header */}
                      <div
                        onClick={() => handleSelectCategory(cat)}
                        style={{
                          background: '#0f172a',
                          padding: '8px 12px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          borderBottom: '1px solid #334155'
                        }}
                      >
                        <span style={{ fontSize: '0.8rem', fontWeight: '800', color: '#f8fafc' }}>{cat}</span>
                        <span style={{ fontSize: '0.7rem', color: allCatSelected ? '#22c55e' : '#94a3b8', fontWeight: '700' }}>
                          {allCatSelected ? '✓ Tout sélectionné' : 'Cliquer pour basculer'}
                        </span>
                      </div>

                      {/* Category Tables List */}
                      <div style={{ padding: '8px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                        {catTables.map(t => {
                          const isChecked = !!selectedTables[t.name];
                          return (
                            <label
                              key={t.name}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '6px 8px',
                                borderRadius: '6px',
                                background: isChecked ? 'rgba(239, 68, 68, 0.12)' : 'transparent',
                                border: isChecked ? '1px solid rgba(239, 68, 68, 0.35)' : '1px solid transparent',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => handleToggleTable(t.name)}
                                style={{ accentColor: '#ef4444', width: '15px', height: '15px' }}
                              />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '0.78rem', fontWeight: '700', color: isChecked ? '#fff' : '#cbd5e1', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {t.label}
                                </div>
                                <div style={{ fontSize: '0.68rem', color: '#64748b' }}>
                                  <code>{t.name}</code> ({t.count} ligne{t.count > 1 ? 's' : ''})
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Danger Warning & Confirmation Footer */}
            <div style={{ borderTop: '1px solid #334155', paddingTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ fontSize: '0.78rem', color: '#f59e0b' }}>
                <strong>{selectedCount}</strong> table(s) sélectionnée(s) &bull; <strong>{selectedRowsCount}</strong> enregistrements
              </div>

              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => setShowWipeModal(false)}
                  style={{
                    padding: '8px 14px',
                    background: '#334155',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '700',
                    fontSize: '0.8rem'
                  }}
                >
                  Annuler
                </button>

                <button
                  type="button"
                  disabled={isWiping || selectedCount === 0}
                  onClick={handleWipeSelected}
                  style={{
                    padding: '8px 18px',
                    background: selectedCount > 0 ? '#ef4444' : '#64748b',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: selectedCount > 0 ? 'pointer' : 'not-allowed',
                    fontWeight: '800',
                    fontSize: '0.8rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {isWiping ? (
                    <>
                      <RefreshCw size={14} className="spin" />
                      <span>Suppression...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 size={14} />
                      <span>Vider les {selectedCount} tables sélectionnées</span>
                    </>
                  )}
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
